import { Injectable } from '@angular/core';
import { FundOverviewService } from '../../services/fund-overview.service';
import fundSummaryQuery from '@graphql/fund-header/fund-summary.graphql';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { PerformanceDetail, Product, SalesChargeBreakPoint } from '@models';
import {
  ConfigurationId,
  FundSummaryState,
  ProductDetailConfiguration,
  ProductType,
  ProductTypes,
} from '@types';
import { SiteConfigService } from '@services/site-config.service';
import { parseNumber } from '@utils/text/number-utils';
import { TranslateService } from '@shared/translate/translate.service';

const logger = Logger.getLogger('FundSummaryService');

const NAV_VISIBILITY: ConfigurationId[] = [
  ConfigurationId.MF,
  ConfigurationId.CEF,
  ConfigurationId.ETF,
  ConfigurationId.VIP,
  ConfigurationId._529,
  ConfigurationId.TIF,
  ConfigurationId.IF,
  ConfigurationId.TOF,
];

const POP_VISIBILITY: ConfigurationId[] = [
  ConfigurationId.MF,
  ConfigurationId.IF,
  ConfigurationId.TIF,
  ConfigurationId._529,
  ConfigurationId.VIP,
];

const MARKET_PRICE_VISIBILITY: ConfigurationId[] = [
  ConfigurationId.CEF,
  ConfigurationId.ETF,
];

const GROSS_VISIBILITY: ConfigurationId[] = [ConfigurationId.SMA];

const PURE_GROSS_VISIBILITY: ConfigurationId[] = [ConfigurationId.SMA];

const NET_VISIBILITY: ConfigurationId[] = [ConfigurationId.SMA];

const MONEY_NAV_VISIBILITY: ConfigurationId[] = [ConfigurationId.MMF];
const LIQUID_ASSET_DAILY_VISIBILITY: ConfigurationId[] = [ConfigurationId.MMF];

const LIQUID_ASSET_WEEKLY_VISIBILITY: ConfigurationId[] = [ConfigurationId.MMF];

const FUND_LEVEL_MARKET_VISIBILITY: ConfigurationId[] = [ConfigurationId.MMF];

const MARKET_TO_MARKET_VISIBILITY: ConfigurationId[] = [ConfigurationId.MMF];

const MASTER_FUND_LEVEL_MARKET_VISIBILITY: ConfigurationId[] = [
  ConfigurationId.MMF,
];

const INTRADAY_VISIBILITY: ConfigurationId[] = [ConfigurationId.MMF];

const REDEMPTION_PRICE_VISIBILITY: ConfigurationId[] = [
  ConfigurationId.MF,
  ConfigurationId.IF,
];

const PREMIUM_DISCOUNT_VISIBILITY: ConfigurationId[] = [ConfigurationId.CEF];

@Injectable({
  providedIn: 'root',
})
export class FundSummaryService {
  private state: FundSummaryState = {
    data: {},
    shareClassProductType: null,
    calcTypeVisibility: {
      show3YrAnnualReturn: null,
      showNav: null,
      showNavYTD: null,
      showNavYTDAsOfDate: null,
      showDistributionRateAtNav: null,
      showPop: null,
      showMarketPrice: null,
      showPureGross: null,
      showGross: null,
      showNet: null,
      showMoneyFundnav: null,
      showLiquidAssetDaily: null,
      showLiquidAssetWeekly: null,
      showFundLevelMarket: null,
      showMarketToMarketNav: null,
      showMasterFundLevelMarket: null,
      showIntraDayNav: null,
      showRedemptionPrice: null,
      showPremiumDiscount: null,
      showNavChangePercentage: null,
      showNavChangeValue: null,
      showSalesChargeBreakPoints: null,
    },
    isSiteIntl: null,
    showRatings: false,
  };
  private fundSummaryStateSubject: Subject<FundSummaryState>;

  constructor(
    private fundOverviewService: FundOverviewService,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService
  ) {
    this.fundSummaryStateSubject = new Subject();
  }

  public get fundSummaryState(): Subject<FundSummaryState> {
    return this.fundSummaryStateSubject;
  }

  /**
   * Call register method of fund detail service.
   * @param fundDataServiceParams recieved as fund parameters
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    this.fundOverviewService
      .register(fundSummaryQuery, {
        fundId: productDetailConfiguration.fundId,
        shareClassCode: productDetailConfiguration.shareClassCode,
      })
      .subscribe((product: Product) => {
        logger.debug(product);
        if (product) {
          this.mapFundSummaryState(product, productDetailConfiguration);
        }
      });
  }

  mapFundSummaryState(
    product: Product,
    productDetailConfiguration: ProductDetailConfiguration
  ) {
    this.state.data.nav = product.shareClasses?.[0]?.nav;
    // 8023: POP/MarketPrice
    this.state.data.distributionRateAtMarketPrice = this.getSalesChargeBreakPoints(
      product.shareClasses?.[0]?.salesChargeBreakPoints,
      8023
    );
    // 8012: NAV
    this.state.data.distributionRateAtNav = this.getSalesChargeBreakPoints(
      product.shareClasses?.[0]?.salesChargeBreakPoints,
      8012
    );
    this.state.data.ratings = product.shareClasses?.[0]?.ratings;
    product.shareClasses?.[0]?.performance?.quarterEnd?.forEach(
      (quarterEndData: PerformanceDetail) => {
        if (quarterEndData?.calcTypeStd === 'PUREGROSS') {
          this.state.data.pureGross = quarterEndData;
        }

        if (quarterEndData?.calcTypeStd === 'GROSS') {
          this.state.data.gross = quarterEndData;
        }

        if (quarterEndData?.calcTypeStd === 'NET') {
          this.state.data.net = quarterEndData;
        }
      }
    );

    // WDE-4201 set performance month-end data
    this.state.data.performance = {};
    if (product.shareClasses?.[0]?.performance?.monthEnd) {
      this.state.data.performance.monthEnd = product.shareClasses?.[0]?.performance?.monthEnd.find(
        (detail: PerformanceDetail): boolean => detail.calcTypeStd === 'NET'
      );
    }
    if (product.shareClasses?.[0]?.performance?.quarterEnd) {
      this.state.data.performance.quarterEnd = product.shareClasses?.[0]?.performance?.quarterEnd.find(
        (detail: PerformanceDetail): boolean => detail.calcTypeStd === 'NET'
      );
    }

    this.state.isSiteIntl = this.siteConfigService.isSiteInternational();

    // WDE-5195: Post introduction of hybrid funds, we now can't rely on fund level product type.
    //           Instead, we've to check product type at share class level.
    //           This will ensure correct display for fund summary and its subsequent configurations.
    this.state.shareClassProductType = product.shareClasses?.[0]
      .productType as ConfigurationId;

    if (
      this.siteConfigService.showOverviewRatings(
        product.productTaxonomy,
        'morningStar'
      ) &&
      !this.siteConfigService.isTemit() &&
      !this.siteConfigService.isMartinCurrieGpt()
    ) {
      if (
        parseNumber(
          product.shareClasses[0].ratings?.morningStarRatingOverall
        ) <= 2 &&
        this.siteConfigService.product.general.suppress1And2StarMSRatings
      ) {
        this.state.showRatings = false;
      } else {
        this.state.showRatings = true;
      }
    } else {
      this.state.showRatings = false;
    }

    // set visibility flags for summary.
    // WDE-4201 temp for CPREIF and for TOF(product type)
    this.state.calcTypeVisibility.show3YrAnnualReturn = this.siteConfigService.showSummaryItem3YearAnnual(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showNav =
      NAV_VISIBILITY.includes(this.state.shareClassProductType) &&
      !this.siteConfigService.hideSummaryItemNav(
        this.state.shareClassProductType
      );
    this.state.calcTypeVisibility.showNavYTDAsOfDate = this.siteConfigService.showSummaryItemNavYtdAsOfDate(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showNavYTD = NAV_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showSalesChargeBreakPoints =
      this.state.shareClassProductType === ConfigurationId.CEF &&
      !this.siteConfigService.isSiteInternational();
    // WDE-4201 temp for CPREIF and for TOF(product type)
    this.state.calcTypeVisibility.showDistributionRateAtNav = this.siteConfigService.showSummaryItemDistributionRateAtNav(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showPop =
      POP_VISIBILITY.includes(this.state.shareClassProductType) &&
      this.siteConfigService.showPop() &&
      !this.siteConfigService.hideSummaryItemPop(
        this.state.shareClassProductType
      );
    this.state.calcTypeVisibility.showMarketPrice =
      (!this.siteConfigService.isSiteUcits() &&
        MARKET_PRICE_VISIBILITY.includes(this.state.shareClassProductType) &&
        this.siteConfigService.isTraded(
          product.shareClasses[0]?.identifiers?.fundId
        )) ||
      this.siteConfigService.isTemit() ||
      this.siteConfigService.isMartinCurrieGpt();
    this.state.calcTypeVisibility.showPureGross = PURE_GROSS_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showGross = GROSS_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showNet = NET_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showMoneyFundnav = MONEY_NAV_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showLiquidAssetDaily = LIQUID_ASSET_DAILY_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showLiquidAssetWeekly = LIQUID_ASSET_WEEKLY_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showFundLevelMarket = FUND_LEVEL_MARKET_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showMarketToMarketNav = MARKET_TO_MARKET_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showMasterFundLevelMarket = MASTER_FUND_LEVEL_MARKET_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showIntraDayNav = INTRADAY_VISIBILITY.includes(
      this.state.shareClassProductType
    );
    this.state.calcTypeVisibility.showRedemptionPrice =
      REDEMPTION_PRICE_VISIBILITY.includes(this.state.shareClassProductType) &&
      this.siteConfigService.showRedemptionPrice();

    this.state.calcTypeVisibility.showPremiumDiscount =
      PREMIUM_DISCOUNT_VISIBILITY.includes(this.state.shareClassProductType) ||
      this.siteConfigService.isTemit() ||
      this.siteConfigService.isMartinCurrieGpt();

    this.state.calcTypeVisibility.showNavChangePercentage =
      this.siteConfigService.isTemit() ||
      this.siteConfigService.isMartinCurrieGpt();

    this.state.calcTypeVisibility.showNavChangeValue =
      !this.siteConfigService.isTemit() &&
      !this.siteConfigService.isMartinCurrieGpt();

    this.fundSummaryStateSubject.next(this.state);
  }

  private getSalesChargeBreakPoints(
    salesChargeBreakPoints: SalesChargeBreakPoint[],
    breakPointTypeKey: number
  ): SalesChargeBreakPoint {
    return salesChargeBreakPoints && salesChargeBreakPoints.length > 0
      ? salesChargeBreakPoints?.find((salesChargeBreakPoint) =>
          [breakPointTypeKey].includes(salesChargeBreakPoint.breakPointTypeKey)
        )
      : this.setNASalesChargeData();
  }

  private setNASalesChargeData(): SalesChargeBreakPoint {
    const salesChargeBreakPoints: SalesChargeBreakPoint = {
      distributionRateBreakPoint: this.translateService.instant('products.NA'),
    };
    return salesChargeBreakPoints;
  }
}
